import {
  DistributionBarChart,
  MetricsCard,
  PageHeader,
  type SelectOption,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { memo, useCallback, useEffect, useMemo, useState } from "react"
import { SearchInput } from "../components/SearchInput"
import { getUrlParams, updateUrlParams } from "../hooks/useUrlParams"
import { useInfiniteScroll } from "../hooks/useVirtualization"
import { getYesterday } from "../lib/dateCalculations"
import { getFromStorage, StorageKeys, saveToStorage } from "../lib/localStorage"
import {
  calculateAverageDailyPerformance,
  calculateMetrics,
  calculatePerformanceScore,
  type PerformanceScore,
} from "../lib/performanceScore"
import type { ClassificationType } from "../stores/SettingsStore"
import {
  useDataStore,
  useI18nStore,
  useSettingsStore,
  useStore,
  useViewStore,
} from "../stores/StoreContext"
import { routes } from "./routes"

type TimeRange = "7days" | "14days" | "28days" | "8weeks" | "12weeks"

// Valid values for URL params
const validTimeRanges: TimeRange[] = ["7days", "14days", "28days", "8weeks", "12weeks"]
const validTabs = ["performance", "score", "prs"]

interface ContributorStats {
  authorHandle: string
  score: number
  prCount: number
  scoreHistory: Array<{ label: string; value: number }>
  prHistory: Array<{ label: string; value: number }>
  performanceScore: PerformanceScore
  performanceHistory: Array<{ label: string; value: number }>
}

// Pre-computed card data to avoid inline calculations during render
interface ContributorCardData {
  authorHandle: string
  displayValue: string
  percentage: number
  trend: "up" | "down" | "neutral"
  chartData: Array<{ label: string; value: number }>
  githubUrl: string
  subtitle: string
}

// Memoized card component to prevent re-renders when data hasn't changed
const ContributorCard = memo(
  ({ data, onNavigate }: { data: ContributorCardData; onNavigate: (handle: string) => void }) => {
    // Helper to safely encode GitHub handles for URLs
    const encodeGitHubHandle = (handle: string): string => {
      return encodeURIComponent(handle.replace(/[^a-zA-Z0-9-]/g, ""))
    }

    // Helper to get GitHub avatar URL with validation
    const getGitHubAvatarUrl = (handle: string, size = 80): string => {
      const safeHandle = encodeGitHubHandle(handle)
      return `https://github.com/${safeHandle}.png?size=${size}`
    }

    const handleClick = () => {
      onNavigate(data.authorHandle)
    }

    return (
      <button type="button" className="cursor-pointer w-full text-left" onClick={handleClick}>
        <MetricsCard
          title={data.authorHandle}
          description={data.displayValue}
          subtitle={data.subtitle}
          percentage={data.percentage}
          trend={data.trend}
          chartData={data.chartData}
          chartType="line"
          avatar={{
            src: getGitHubAvatarUrl(data.authorHandle),
            fallback: data.authorHandle.slice(0, 2).toUpperCase(),
            alt: data.authorHandle,
          }}
        />
      </button>
    )
  },
  (prev, next) =>
    prev.data.authorHandle === next.data.authorHandle &&
    prev.data.displayValue === next.data.displayValue &&
    prev.data.percentage === next.data.percentage &&
    prev.data.trend === next.data.trend &&
    prev.data.subtitle === next.data.subtitle &&
    prev.data.chartData === next.data.chartData,
)

// Helper to get date range start based on time range
// Uses yesterday as the reference point to exclude the current incomplete day
const getDateRangeStart = (timeRange: TimeRange): Date => {
  const yesterday = getYesterday()

  if (timeRange === "7days") {
    return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // 7 days including yesterday
  }
  if (timeRange === "14days") {
    return new Date(yesterday.getTime() - 13 * 24 * 60 * 60 * 1000) // 14 days including yesterday
  }
  if (timeRange === "28days") {
    return new Date(yesterday.getTime() - 27 * 24 * 60 * 60 * 1000) // 28 days including yesterday
  }
  if (timeRange === "8weeks") {
    return new Date(yesterday.getTime() - (8 * 7 - 1) * 24 * 60 * 60 * 1000) // 8 weeks including yesterday
  }
  if (timeRange === "12weeks") {
    return new Date(yesterday.getTime() - (12 * 7 - 1) * 24 * 60 * 60 * 1000) // 12 weeks including yesterday
  }
  return new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000) // Default to 7 days
}

// Helper to determine chart granularity and period based on available data
const getChartPeriod = (
  timeRange: TimeRange,
): { periods: number; unit: "day" | "week" | "month" } => {
  // For the new time ranges, use simple day-based periods
  if (timeRange === "7days") {
    return { periods: 7, unit: "day" }
  }
  if (timeRange === "14days") {
    return { periods: 14, unit: "day" }
  }
  if (timeRange === "28days") {
    return { periods: 28, unit: "day" }
  }
  if (timeRange === "8weeks") {
    return { periods: 8, unit: "week" }
  }
  if (timeRange === "12weeks") {
    return { periods: 12, unit: "week" }
  }
  return { periods: 7, unit: "day" } // Default
}

// Helper to format date labels
const formatDateLabel = (date: Date, unit: "day" | "week" | "month"): string => {
  if (unit === "month") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    })
  }
  if (unit === "week") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

// Helper to get period start date
const getPeriodStart = (
  periodIndex: number,
  totalPeriods: number,
  unit: "day" | "week" | "month",
): Date => {
  const yesterday = getYesterday()
  const periodOffset = totalPeriods - periodIndex - 1

  if (unit === "day") {
    return new Date(yesterday.getTime() - periodOffset * 24 * 60 * 60 * 1000)
  }
  if (unit === "week") {
    return new Date(yesterday.getTime() - periodOffset * 7 * 24 * 60 * 60 * 1000)
  }
  // month
  const date = new Date(yesterday)
  date.setMonth(date.getMonth() - periodOffset)
  return date
}

export const ContributorsPage = observer(() => {
  const store = useStore()
  const dataStore = useDataStore()
  const i18nStore = useI18nStore()
  const settingsStore = useSettingsStore()
  const viewStore = useViewStore()

  // Initialize from localStorage first, then override with URL params
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>(() => {
    const saved = getFromStorage(StorageKeys.CONTRIBUTORS_TIME_RANGE) as TimeRange | null
    return saved && validTimeRanges.includes(saved) ? saved : "7days"
  })
  const [selectedTab, setSelectedTab] = useState("performance")
  const [searchQuery, setSearchQuery] = useState("")

  // Navigate to individual contributor dashboard
  const navigateToContributor = useCallback(
    (handle: string) => {
      // Get current time range to preserve it during navigation
      const currentTimeRange = viewStore.timeRange
      // Update URL params to show individual view with selected contributor and current time range
      // Navigate to home route - query params are preserved automatically
      store.router.goTo(routes.home)
      updateUrlParams({ view: "individual", contributor: handle, timeRange: currentTimeRange })
    },
    [store.router, viewStore.timeRange],
  )

  // Initialize from URL params on mount (URL params take precedence over localStorage)
  useEffect(() => {
    const params = getUrlParams()

    // Set time range from URL if valid, otherwise keep localStorage value
    if (params.timeRange && validTimeRanges.includes(params.timeRange as TimeRange)) {
      setSelectedTimeRange(params.timeRange as TimeRange)
    }

    // Set tab from URL if valid
    if (params.tab && validTabs.includes(params.tab)) {
      setSelectedTab(params.tab)
    }
  }, [])

  // Handle time range change with URL and localStorage update
  const handleTimeRangeChange = useCallback((id: string) => {
    setSelectedTimeRange(id as TimeRange)
    saveToStorage(StorageKeys.CONTRIBUTORS_TIME_RANGE, id)
    updateUrlParams({ timeRange: id === "30days" ? undefined : id })
  }, [])

  // Handle tab change with URL update
  const handleTabChange = useCallback((tab: string) => {
    setSelectedTab(tab)
    updateUrlParams({ tab: tab === "performance" ? undefined : tab })
  }, [])

  // Search handler
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query.toLowerCase())
  }, [])

  // Time range options for the dropdown (matching Dashboard)
  const timeRangeOptions: SelectOption[] = [
    { id: "7days", label: i18nStore.t("timeLast7Days") },
    { id: "14days", label: i18nStore.t("timeLast14Days") },
    { id: "28days", label: i18nStore.t("timeLast28Days") },
    { id: "8weeks", label: i18nStore.t("timeLast8Weeks") },
    { id: "12weeks", label: i18nStore.t("timeLast12Weeks") },
  ]

  // Calculate date range based on selection
  const dateRange = useMemo(() => {
    return getDateRangeStart(selectedTimeRange)
  }, [selectedTimeRange])

  // Get the timestamp for dependency tracking (Date objects don't work well in deps)
  const dateRangeTimestamp = dateRange.getTime()

  // Calculate the time range in days for performance score normalization
  const timeRangeDays = useMemo(() => {
    if (selectedTimeRange === "7days") return 7
    if (selectedTimeRange === "14days") return 14
    if (selectedTimeRange === "28days") return 28
    if (selectedTimeRange === "8weeks") return 8 * 7 // 56 days
    if (selectedTimeRange === "12weeks") return 12 * 7 // 84 days
    return 7 // Default
  }, [selectedTimeRange, dataStore.allCommits, dataStore.allPullRequests])

  // Determine chart period configuration
  const chartConfig = useMemo(() => {
    return getChartPeriod(selectedTimeRange)
  }, [selectedTimeRange])

  // Calculate contributor statistics with historical data
  // biome-ignore lint/correctness/useExhaustiveDependencies: MobX observable arrays require snapshot conversion via .slice()
  const contributorStats = useMemo(() => {
    // Get selected classifications from settings
    const selectedClassifications = settingsStore.selectedClassificationsArray

    // Create a snapshot of the data to avoid MobX reactivity issues
    const allCommits = dataStore.allCommits.slice()
    const pullRequests = dataStore.allPullRequests.slice()
    const issues = dataStore.allIssues.slice()

    // Filter commits by selected classifications
    const commits = allCommits.filter((c) => {
      const classType = (c.commitType || "unclassified") as ClassificationType
      return selectedClassifications.includes(classType)
    })

    const statsMap = new Map<string, ContributorStats>()
    const { periods, unit } = chartConfig

    // Initialize periods for all contributors
    const initializeContributor = (handle: string): ContributorStats => {
      const history: Array<{ label: string; value: number }> = []

      for (let i = 0; i < periods; i++) {
        const periodStart = getPeriodStart(i, periods, unit)
        history.push({
          label: formatDateLabel(periodStart, unit),
          value: 0,
        })
      }

      // Initialize with empty performance score
      const emptyMetrics = calculateMetrics([], [], [])
      const emptyScore = calculatePerformanceScore(emptyMetrics, timeRangeDays)

      return {
        authorHandle: handle,
        score: 0,
        prCount: 0,
        scoreHistory: history.map((h) => ({ ...h })),
        prHistory: history.map((h) => ({ ...h })),
        performanceScore: emptyScore,
        performanceHistory: history.map((h) => ({ ...h })),
      }
    }

    // Helper to get period index for a date (using yesterday as reference)
    const getPeriodIndex = (date: Date): number => {
      const yesterday = getYesterday()
      let diff: number

      if (unit === "day") {
        diff = Math.floor((yesterday.getTime() - date.getTime()) / (24 * 60 * 60 * 1000))
      } else if (unit === "week") {
        diff = Math.floor((yesterday.getTime() - date.getTime()) / (7 * 24 * 60 * 60 * 1000))
      } else {
        // month
        const monthsDiff =
          (yesterday.getFullYear() - date.getFullYear()) * 12 +
          (yesterday.getMonth() - date.getMonth())
        diff = monthsDiff
      }

      const periodIndex = periods - diff - 1
      return periodIndex >= 0 && periodIndex < periods ? periodIndex : -1
    }

    // Track data per contributor per period for performance score calculation
    const contributorDataByPeriod = new Map<
      string,
      Map<
        number,
        {
          commits: Array<{
            score: number
            insertions: number
            deletions: number
            filesChanged: number
            commitType?: string
          }>
          prs: Array<{ additions: number; deletions: number; state: string }>
          issues: Array<{ state: string }>
        }
      >
    >()

    // Initialize data structure
    const getContributorPeriodData = (handle: string, periodIndex: number) => {
      if (!contributorDataByPeriod.has(handle)) {
        contributorDataByPeriod.set(handle, new Map())
      }
      const periodMap = contributorDataByPeriod.get(handle)
      if (!periodMap) {
        throw new Error("Period map should exist")
      }
      if (!periodMap.has(periodIndex)) {
        periodMap.set(periodIndex, { commits: [], prs: [], issues: [] })
      }
      const periodData = periodMap.get(periodIndex)
      if (!periodData) {
        throw new Error("Period data should exist")
      }
      return periodData
    }

    // Process commits for scores and track by period
    commits.forEach((commit) => {
      if (commit.timestampDate >= dateRange) {
        const current =
          statsMap.get(commit.authorHandle) || initializeContributor(commit.authorHandle)
        current.score += commit.score || 0

        // Add to historical data
        const periodIndex = getPeriodIndex(commit.timestampDate)
        if (periodIndex >= 0) {
          current.scoreHistory[periodIndex].value += commit.score || 0

          // Track commit data for performance score calculation
          const periodData = getContributorPeriodData(commit.authorHandle, periodIndex)
          periodData.commits.push({
            score: commit.score || 0,
            insertions: commit.insertions || 0,
            deletions: commit.deletions || 0,
            filesChanged: commit.filesChanged || 0,
            commitType: commit.commitType,
          })
        }

        statsMap.set(commit.authorHandle, current)
      }
    })

    // Process PRs for counts (merged PRs)
    pullRequests.forEach((pr) => {
      if (pr.state === "merged" && pr.mergedAtDate && pr.mergedAtDate >= dateRange) {
        const current = statsMap.get(pr.creatorHandle) || initializeContributor(pr.creatorHandle)
        current.prCount += 1

        // Add to historical data
        const periodIndex = getPeriodIndex(pr.mergedAtDate)
        if (periodIndex >= 0) {
          current.prHistory[periodIndex].value += 1

          // Track PR data for performance score calculation
          const periodData = getContributorPeriodData(pr.creatorHandle, periodIndex)
          periodData.prs.push({
            additions: pr.additions || 0,
            deletions: pr.deletions || 0,
            state: pr.state,
          })
        }

        statsMap.set(pr.creatorHandle, current)
      }
    })

    // Process issues (all within date range)
    issues.forEach((issue) => {
      if (issue.closedAtDate && issue.closedAtDate >= dateRange) {
        const handle = issue.closedByHandle || issue.creatorHandle
        const current = statsMap.get(handle) || initializeContributor(handle)

        // Add to historical data
        const periodIndex = getPeriodIndex(issue.closedAtDate)
        if (periodIndex >= 0) {
          // Track issue data for performance score calculation
          const periodData = getContributorPeriodData(handle, periodIndex)
          periodData.issues.push({
            state: issue.state,
          })
        }

        statsMap.set(handle, current)
      }
    })

    // Calculate performance scores for each contributor
    for (const [handle, contributor] of statsMap.entries()) {
      // Get all commits, PRs, and issues for this contributor in the selected time range
      const contributorCommits = commits.filter((c) => c.authorHandle === handle)
      const contributorPRs = pullRequests.filter((pr) => pr.creatorHandle === handle)
      const contributorIssues = issues.filter((issue) => {
        const issueHandle = issue.closedByHandle || issue.creatorHandle
        return issueHandle === handle
      })

      // Calculate average daily performance score (excluding weekends with no activity)
      // Note: calculateAverageDailyPerformance now defaults to yesterday as end date
      const avgDailyScore = calculateAverageDailyPerformance(
        contributorCommits,
        contributorPRs,
        contributorIssues,
        dateRange,
      )

      // Create a performance score object with the calculated average
      contributor.performanceScore = {
        overall: avgDailyScore,
        breakdown: {
          quality: 0,
          throughput: 0,
          impact: 0,
          problemSolving: 0,
          versatility: 0,
        },
        metrics: {
          commitScore: 0,
          commitCount: 0,
          linesAdded: 0,
          linesRemoved: 0,
          filesChanged: 0,
          prCount: 0,
          prAdditions: 0,
          prDeletions: 0,
          issuesResolved: 0,
          featureCommits: 0,
          bugfixCommits: 0,
          refactoringCommits: 0,
          otherCommits: 0,
        },
      }

      // Calculate performance score history per period
      const periodMap = contributorDataByPeriod.get(handle)
      for (let i = 0; i < periods; i++) {
        const periodData = periodMap?.get(i)
        if (periodData) {
          const periodMetrics = calculateMetrics(
            periodData.commits,
            periodData.prs,
            periodData.issues,
          )
          // For historical periods, use the unit-based duration
          const periodDays = unit === "day" ? 1 : unit === "week" ? 7 : 30
          const periodScore = calculatePerformanceScore(periodMetrics, periodDays)
          contributor.performanceHistory[i].value = periodScore.overall
        }
      }
    }

    return Array.from(statsMap.values())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRangeTimestamp,
    timeRangeDays,
    chartConfig.periods,
    chartConfig.unit,
    // Force recalculation when data changes
    dataStore.allCommits.length,
    dataStore.allPullRequests.length,
    dataStore.allIssues.length,
    settingsStore.selectedClassificationsArray.length,
  ])

  // Pre-compute all stats and card data in a single useMemo to avoid redundant work
  const { performanceCards, scoreCards, prCards, performanceStats, scoreStats, prStats } =
    useMemo(() => {
      // Calculate averages first
      const totalPerformance = contributorStats.reduce(
        (sum, c) => sum + c.performanceScore.overall,
        0,
      )
      const totalScore = contributorStats.reduce((sum, c) => sum + c.score, 0)
      const totalPRs = contributorStats.reduce((sum, c) => sum + c.prCount, 0)
      const count = contributorStats.length || 1

      const perfAverage = totalPerformance / count
      const scoreAverage = totalScore / count
      const prAverage = totalPRs / count

      // Sort once for each metric
      const perfSorted = [...contributorStats].sort(
        (a, b) => b.performanceScore.overall - a.performanceScore.overall,
      )
      const scoreSorted = [...contributorStats].sort((a, b) => b.score - a.score)
      const prSorted = [...contributorStats].sort((a, b) => b.prCount - a.prCount)

      // Pre-compute card data to avoid inline calculations during render
      const computeTrend = (
        value: number,
        average: number,
      ): { percentage: number; trend: "up" | "down" | "neutral" } => {
        const percentDiff = average > 0 ? ((value - average) / average) * 100 : 0
        const trend: "up" | "down" | "neutral" =
          percentDiff > 0 ? "up" : percentDiff < 0 ? "down" : "neutral"
        return { percentage: Number(percentDiff.toFixed(1)), trend }
      }

      // Helper function to filter out weekends with no activity from chart data
      // Only applies to day-based periods
      const filterWeekendInactivity = (
        chartData: Array<{ label: string; value: number }>,
      ): Array<{ label: string; value: number }> => {
        if (chartConfig.unit !== "day") {
          return chartData // Only filter for day-based periods
        }

        return chartData.filter((point, index) => {
          // Calculate the date for this period
          const periodStart = getPeriodStart(index, chartConfig.periods, chartConfig.unit)
          const dayOfWeek = periodStart.getDay()
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

          // Include if it's not a weekend, or if it's a weekend with activity
          return !isWeekend || point.value > 0
        })
      }

      const perfCards: ContributorCardData[] = perfSorted.map((c, index) => {
        const { percentage, trend } = computeTrend(c.performanceScore.overall, perfAverage)
        return {
          authorHandle: c.authorHandle,
          displayValue: c.performanceScore.overall.toFixed(1),
          percentage,
          trend,
          chartData: filterWeekendInactivity(c.performanceHistory),
          githubUrl: `https://github.com/${c.authorHandle}`,
          subtitle: `#${index + 1}`,
        }
      })

      const scCards: ContributorCardData[] = scoreSorted.map((c, index) => {
        const { percentage, trend } = computeTrend(c.score, scoreAverage)
        return {
          authorHandle: c.authorHandle,
          displayValue: c.score.toLocaleString(),
          percentage,
          trend,
          chartData: filterWeekendInactivity(c.scoreHistory),
          githubUrl: `https://github.com/${c.authorHandle}`,
          subtitle: `#${index + 1}`,
        }
      })

      const pCards: ContributorCardData[] = prSorted.map((c, index) => {
        const { percentage, trend } = computeTrend(c.prCount, prAverage)
        return {
          authorHandle: c.authorHandle,
          displayValue: c.prCount.toString(),
          percentage,
          trend,
          chartData: filterWeekendInactivity(c.prHistory),
          githubUrl: `https://github.com/${c.authorHandle}`,
          subtitle: `#${index + 1}`,
        }
      })

      // Pre-compute chart data for distribution bar charts
      const perfChartData = perfSorted.map((c) => ({
        name: c.authorHandle,
        value: Number(c.performanceScore.overall.toFixed(1)),
      }))

      const scoreChartData = scoreSorted.map((c) => ({
        name: c.authorHandle,
        value: c.score,
      }))

      const prChartData = prSorted.map((c) => ({
        name: c.authorHandle,
        value: c.prCount,
      }))

      return {
        performanceCards: perfCards,
        scoreCards: scCards,
        prCards: pCards,
        performanceStats: {
          sorted: perfSorted,
          average: perfAverage,
          total: totalPerformance,
          chartData: perfChartData,
        },
        scoreStats: {
          sorted: scoreSorted,
          average: scoreAverage,
          total: totalScore,
          chartData: scoreChartData,
        },
        prStats: {
          sorted: prSorted,
          average: prAverage,
          total: totalPRs,
          chartData: prChartData,
        },
      }
    }, [contributorStats])

  // Filter cards based on search query
  const filteredPerformanceCards = useMemo(() => {
    if (!searchQuery) return performanceCards
    return performanceCards.filter((card) => card.authorHandle.toLowerCase().includes(searchQuery))
  }, [performanceCards, searchQuery])

  const filteredScoreCards = useMemo(() => {
    if (!searchQuery) return scoreCards
    return scoreCards.filter((card) => card.authorHandle.toLowerCase().includes(searchQuery))
  }, [scoreCards, searchQuery])

  const filteredPrCards = useMemo(() => {
    if (!searchQuery) return prCards
    return prCards.filter((card) => card.authorHandle.toLowerCase().includes(searchQuery))
  }, [prCards, searchQuery])

  // Use infinite scroll for each tab
  const {
    visibleItems: visiblePerformanceCards,
    sentinelRef: perfSentinelRef,
    hasMore: hasMorePerf,
  } = useInfiniteScroll(filteredPerformanceCards)

  const {
    visibleItems: visibleScoreCards,
    sentinelRef: scoreSentinelRef,
    hasMore: hasMoreScore,
  } = useInfiniteScroll(filteredScoreCards)

  const {
    visibleItems: visiblePrCards,
    sentinelRef: prSentinelRef,
    hasMore: hasMorePr,
  } = useInfiniteScroll(filteredPrCards)

  return (
    <div className="min-h-full bg-background">
      <div className="container mx-auto p-3 sm:p-4 md:p-6">
        <PageHeader
          title={i18nStore.t("contributorsTitle")}
          subtitle={i18nStore.t("contributorsSubtitle")}
          selectOptions={timeRangeOptions}
          selectedOptionId={selectedTimeRange}
          onSelectChange={handleTimeRangeChange}
          selectPlaceholder={i18nStore.t("selectTimeRange")}
        >
          <Tabs value={selectedTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full max-w-2xl grid-cols-3">
              <TabsTrigger value="performance">{i18nStore.t("tabPerformance")}</TabsTrigger>
              <TabsTrigger value="score">{i18nStore.t("tabScore")}</TabsTrigger>
              <TabsTrigger value="prs">{i18nStore.t("tabPRs")}</TabsTrigger>
            </TabsList>

            {/* Search Input */}
            <div className="mt-4">
              <SearchInput
                placeholder={i18nStore.t("searchContributors")}
                onSearch={handleSearch}
                className="max-w-md"
              />
            </div>

            {/* Performance Tab */}
            <TabsContent value="performance" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">
                  {i18nStore.t("performanceScoreTitle")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("performanceScoreDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("performanceScoreAverage", {
                      value: performanceStats.average.toFixed(1),
                    })}
                  </span>{" "}
                  {i18nStore.t("performanceScoreComparison")}
                </p>
              </div>

              {/* Distribution bar chart */}
              <DistributionBarChart
                data={performanceStats.chartData}
                height={180}
                showAverageLine={true}
                renderAvatars={true}
                avatarType="contributor"
              />

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visiblePerformanceCards.map((cardData) => (
                  <ContributorCard
                    key={cardData.authorHandle}
                    data={cardData}
                    onNavigate={navigateToContributor}
                  />
                ))}
              </div>

              {/* Sentinel for infinite scroll */}
              {hasMorePerf && <div ref={perfSentinelRef} className="h-4" />}

              {filteredPerformanceCards.length === 0 && searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("noSearchResults")}
                </div>
              )}

              {performanceStats.sorted.length === 0 && !searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("contributorsNoData")}
                </div>
              )}
            </TabsContent>

            {/* Score Tab */}
            <TabsContent value="score" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">{i18nStore.t("scoreTitle")}</h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("scoreDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("scoreAverage", {
                      value: scoreStats.average.toFixed(0),
                    })}
                  </span>{" "}
                  {i18nStore.t("scoreComparison")}
                </p>
              </div>

              {/* Distribution bar chart */}
              <DistributionBarChart
                data={scoreStats.chartData}
                height={180}
                showAverageLine={true}
                renderAvatars={true}
                avatarType="contributor"
              />

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visibleScoreCards.map((cardData) => (
                  <ContributorCard
                    key={cardData.authorHandle}
                    data={cardData}
                    onNavigate={navigateToContributor}
                  />
                ))}
              </div>

              {/* Sentinel for infinite scroll */}
              {hasMoreScore && <div ref={scoreSentinelRef} className="h-4" />}

              {filteredScoreCards.length === 0 && searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("noSearchResults")}
                </div>
              )}

              {scoreStats.sorted.length === 0 && !searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("contributorsNoData")}
                </div>
              )}
            </TabsContent>

            {/* PRs Tab */}
            <TabsContent value="prs" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">{i18nStore.t("prsTitle")}</h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("prsDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("prsAverage", {
                      value: prStats.average.toFixed(1),
                    })}
                  </span>{" "}
                  {i18nStore.t("prsComparison")}
                </p>
              </div>

              {/* Distribution bar chart */}
              <DistributionBarChart
                data={prStats.chartData}
                height={180}
                showAverageLine={true}
                renderAvatars={true}
                avatarType="contributor"
              />

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visiblePrCards.map((cardData) => (
                  <ContributorCard
                    key={cardData.authorHandle}
                    data={cardData}
                    onNavigate={navigateToContributor}
                  />
                ))}
              </div>

              {/* Sentinel for infinite scroll */}
              {hasMorePr && <div ref={prSentinelRef} className="h-4" />}

              {filteredPrCards.length === 0 && searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("noSearchResults")}
                </div>
              )}

              {prStats.sorted.length === 0 && !searchQuery && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("contributorsNoData")}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </PageHeader>
      </div>
    </div>
  )
})

ContributorsPage.displayName = "ContributorsPage"
